// A customer's tokens in Admin › Users (TOKEN_LEDGER_PLAN.md §6.4): each lot with what is
// left and when it lapses; Refund on a bought pack (only an admin authorises and carries out
// a refund: Tony, 30 Oct 2026), shown with what it pays back and takes before confirming;
// Give tokens for goodwill. Both need a reason, recorded with the admin.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';

interface Lot {
  id: string; kind: string; amount: number; remaining: number; expiresAt: string | null;
  source: string; sourceRef: string | null; refundedAt: string | null; createdAt: string; refundable: boolean;
}

const KIND: Record<string, string> = { standard: 'Plan allowance', topup: 'Pack bought', grant: 'Goodwill', migrated: 'Carried over' };
const n = (v: number) => v.toLocaleString('en-NZ');
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-NZ') : 'Never');
const field = 'px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white text-sm';

export function AdminUserTokens({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const { data: lots = [], isLoading } = useQuery<Lot[]>({
    queryKey: ['admin', 'user', userId, 'tokens'],
    queryFn: () => api.adminGetUserTokens(userId),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin', 'user', userId, 'tokens'] });

  const [refunding, setRefunding] = useState<string | null>(null);
  const [quote, setQuote] = useState<{ tokens: number; amount: number; currency: string } | null>(null);
  const [reason, setReason] = useState('');
  const [give, setGive] = useState({ tokens: '', reason: '' });
  const [message, setMessage] = useState<string | null>(null);

  const startRefund = async (lotId: string) => {
    setRefunding(lotId); setQuote(null); setReason(''); setMessage(null);
    try { setQuote(await api.adminTokenAction(userId, { action: 'quote', lot_id: lotId })); }
    catch (e) { setMessage(errorText(e)); setRefunding(null); }
  };
  const refund = useMutation({
    mutationFn: () => api.adminTokenAction(userId, { action: 'refund', lot_id: refunding!, reason: reason.trim() }),
    onSuccess: (r: { amount: number; currency: string; tokensTaken: number }) => {
      setMessage(`Refunded ${money(r.amount, r.currency)} and took back ${n(r.tokensTaken)} tokens.`);
      setRefunding(null); refresh();
    },
    onError: (e) => setMessage(errorText(e)),
  });
  const giveTokens = useMutation({
    mutationFn: () => api.adminTokenAction(userId, { action: 'give', tokens: parseInt(give.tokens, 10), reason: give.reason.trim() }),
    onSuccess: (r: { tokens: number }) => { setMessage(`Gave ${n(r.tokens)} tokens.`); setGive({ tokens: '', reason: '' }); refresh(); },
    onError: (e) => setMessage(errorText(e)),
  });

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 space-y-4">
      <h4 className="text-base font-medium text-white">Tokens</h4>
      {isLoading ? <p className="text-sm text-slate-400">Loading…</p> : lots.length === 0 ? (
        <p className="text-sm text-slate-400">No tokens in the ledger yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-400">
            <th className="py-1">Kind</th><th>Tokens</th><th>Left</th><th>Lapses</th><th>Added</th><th />
          </tr></thead>
          <tbody>
            {lots.map((l) => (
              <tr key={l.id} className="border-t border-slate-700/50 text-slate-200">
                <td className="py-2">{KIND[l.kind] ?? l.kind}{l.refundedAt && <span className="text-amber-400"> (refunded)</span>}</td>
                <td>{n(l.amount)}</td>
                <td>{n(l.remaining)}</td>
                <td>{day(l.expiresAt)}</td>
                <td>{day(l.createdAt)}</td>
                <td className="text-right">
                  {l.refundable && (
                    <button type="button" className="text-red-400 hover:text-red-300" onClick={() => startRefund(l.id)}>Refund</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {refunding && (
        <div className="p-3 border border-red-500/40 rounded-lg space-y-2">
          {!quote ? <p className="text-sm text-slate-400">Working out the refund…</p> : (
            <>
              <p className="text-sm text-slate-200">
                Refunds the unused part of the pack: {money(quote.amount, quote.currency)}, and takes back {n(quote.tokens)} tokens.
              </p>
              <label className="block text-sm text-slate-300">
                Reason (recorded with your name)
                <input className={`${field} w-full mt-1`} value={reason} onChange={(e) => setReason(e.target.value)} />
              </label>
              <div className="flex gap-2">
                <button type="button" disabled={!reason.trim() || refund.isPending}
                  className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-50"
                  onClick={() => refund.mutate()}>
                  {`Refund ${money(quote.amount, quote.currency)} and take back ${n(quote.tokens)} tokens`}
                </button>
                <button type="button" className="px-3 py-2 text-sm text-slate-300" onClick={() => setRefunding(null)}>Cancel</button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm text-slate-300">
          Tokens to give
          <input type="number" min={1} className={`${field} block mt-1 w-32`} value={give.tokens}
            onChange={(e) => setGive({ ...give, tokens: e.target.value })} />
        </label>
        <label className="text-sm text-slate-300 flex-1 min-w-48">
          Why (recorded with your name)
          <input className={`${field} block mt-1 w-full`} value={give.reason} onChange={(e) => setGive({ ...give, reason: e.target.value })} />
        </label>
        <button type="button" disabled={!(parseInt(give.tokens, 10) > 0) || !give.reason.trim() || giveTokens.isPending}
          className="px-3 py-2 rounded-lg bg-cyan-600 text-white text-sm disabled:opacity-50" onClick={() => giveTokens.mutate()}>
          Give tokens
        </button>
      </div>

      {message && <p className="text-sm text-slate-200" role="status">{message}</p>}
    </div>
  );
}

function money(cents: number, currency: string) {
  return `${currency.toUpperCase()} ${(cents / 100).toFixed(2)}`;
}

function errorText(e: unknown): string {
  const err = e as { response?: { data?: { error?: string } }; message?: string };
  return err.response?.data?.error ?? err.message ?? 'Something went wrong';
}
